import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { AppHeader } from '../components/AppHeader';
import { CustomerPicker } from '../components/CustomerPicker';

/**
 * Landing screen for global-customer users. Mounted at /portal/select and
 * also serves as the post-login destination for any user whose session
 * still needs to pick an active customer. Non-global users (or anyone
 * already past the picker step) are bounced straight to /portal.
 */
export function CustomerSelectPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });

  useEffect(() => {
    if (!meQuery.data) return;
    // Non-global user landed here directly -- send them to /portal because
    // their picker would only ever list their own home tenant.
    if (!meQuery.data.isGlobalUser) {
      navigate('/portal', { replace: true });
    }
  }, [meQuery.data, navigate]);

  if (meQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-wc-gray">
        <p className="text-sm text-wc-muted">Loading…</p>
      </div>
    );
  }
  if (meQuery.isError || !meQuery.data) {
    // Defer to the existing redirect-to-login flow used elsewhere.
    window.location.assign('/');
    return null;
  }

  const me = meQuery.data;
  return (
    <div className="flex min-h-screen flex-col bg-wc-gray">
      <AppHeader me={me} />
      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <CustomerPicker
          activeCustomerId={me.hasPickedActiveCustomer ? me.customer.id : null}
          homeCustomerId={me.homeCustomer.id}
          onPicked={() => {
            // Force a fresh /me read so the header/portal pick up the new
            // active customer immediately after we route away.
            queryClient.invalidateQueries({ queryKey: ['me'] });
            navigate('/portal', { replace: true });
          }}
        />
      </main>
    </div>
  );
}
